import { isRouteErrorResponse } from 'react-router-dom';

/**
 * Helpers for the error page (src/pages/ErrorPage.tsx): what a thrown value
 * says, whether it is a stale-deploy chunk failure, and the one-shot reload
 * that recovers from one.
 */

export interface ErrorDescription {
  name: string;
  message: string;
  stack: string | null;
}

/** Name, message and stack of anything a route can throw. */
export function describeError(error: unknown): ErrorDescription {
  if (isRouteErrorResponse(error)) {
    const detail = typeof error.data === 'string' && error.data ? ` — ${error.data}` : '';
    return { name: 'RouteErrorResponse', message: `${error.status} ${error.statusText}${detail}`.trim(), stack: null };
  }
  if (error instanceof Error) {
    return { name: error.name || 'Error', message: error.message, stack: error.stack ?? null };
  }
  if (typeof error === 'string') return { name: 'Error', message: error, stack: null };
  if (typeof error === 'object' && error !== null) {
    const { name, message, stack } = error as { name?: unknown; message?: unknown; stack?: unknown };
    if (typeof message === 'string') {
      return {
        name: typeof name === 'string' && name ? name : 'Error',
        message,
        stack: typeof stack === 'string' ? stack : null,
      };
    }
  }
  let message: string;
  try {
    message = JSON.stringify(error) ?? String(error);
  } catch {
    message = String(error);
  }
  return { name: 'Error', message, stack: null };
}

// What each browser says when a lazily loaded chunk is no longer on the server
// (a tab opened before a deploy asking for the old build's files).
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite's preload helper, when the page's stylesheet is gone
  /Loading (?:CSS )?chunk \S+ failed/i, // webpack-style ChunkLoadError
];

/** True when the error is a failed dynamic import / chunk load. */
export function isChunkLoadError(error: unknown): boolean {
  const { name, message } = describeError(error);
  return name === 'ChunkLoadError' || CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/** Technical details are for testers: every environment except production. */
export function shouldShowErrorDetails(appEnv: unknown = import.meta.env.VITE_APP_ENV): boolean {
  return String(appEnv ?? '').toLowerCase() !== 'production';
}

const CHUNK_RELOAD_KEY = 'chunk-reload-at';
/** No second automatic reload within this window, so a chunk that is really gone cannot loop. */
export const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

/**
 * Whether an automatic reload is allowed right now. Without sessionStorage
 * there is no loop guard, so the answer is no.
 */
export function canAutoReloadForChunk(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
    return !last || Date.now() - last >= CHUNK_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

/** Takes the one automatic reload: true (and stamps the time) when allowed. */
export function claimAutoReloadForChunk(): boolean {
  if (!canAutoReloadForChunk()) return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

export const reloadPage = (): void => {
  window.location.reload();
};
